function shellQuote(value) {
  if (value === undefined || value === null) {
    return "''";
  }
  return `'${String(value).replace(/'/g, `'\\''`)}'`;
}

function resolveBaseUrl(axiosDefaults) {
  const base = (axiosDefaults && axiosDefaults.baseURL) || '';
  if (/^https?:\/\//i.test(base)) {
    return base.replace(/\/+$/, '');
  }
  const origin = typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost:4203';
  if (!base || base === '/') {
    return origin;
  }
  return `${origin}${base.startsWith('/') ? base : `/${base}`}`.replace(/\/+$/, '');
}

function buildQueryString(params) {
  if (!params || typeof params !== 'object') {
    return '';
  }
  const parts = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') {
      return;
    }
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  });
  return parts.length ? `?${parts.join('&')}` : '';
}

export function buildCurlFromParts({ method, url, headers, body }) {
  const lines = [`curl -X ${method || 'GET'} ${shellQuote(url)}`];
  const headerMap = headers || {};
  Object.keys(headerMap).forEach((name) => {
    const value = headerMap[name];
    if (value !== undefined && value !== null && value !== '') {
      lines.push(`  -H ${shellQuote(`${name}: ${value}`)}`);
    }
  });
  if (body !== undefined && body !== null && method !== 'GET') {
    const payload = typeof body === 'string' ? body : JSON.stringify(body, null, 2);
    lines.push(`  -d ${shellQuote(payload)}`);
  }
  return lines.join(' \\\n');
}

export function buildTrackingGetCurl(path, params, axiosDefaults) {
  const baseUrl = resolveBaseUrl(axiosDefaults);
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${baseUrl}${normalizedPath}${buildQueryString(params)}`;
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };
  const auth = axiosDefaults && axiosDefaults.headers && axiosDefaults.headers.common
    ? axiosDefaults.headers.common.Authorization
    : null;
  if (auth) {
    headers.Authorization = auth;
  }
  return buildCurlFromParts({ method: 'GET', url, headers });
}

export function buildChatAskCurl(payload, axiosDefaults) {
  const baseUrl = resolveBaseUrl(axiosDefaults);
  const url = `${baseUrl}/api/admin/onboarding-chat`;
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };
  const auth = axiosDefaults && axiosDefaults.headers && axiosDefaults.headers.common
    ? axiosDefaults.headers.common.Authorization
    : null;
  if (auth) {
    headers.Authorization = auth;
  }
  return buildCurlFromParts({
    method: 'POST',
    url,
    headers,
    body: payload
  });
}

export function buildCurlFromAxiosError(error, fallbackPayload, axiosDefaults) {
  const config = error && error.config;
  if (config && config.url) {
    const baseUrl = resolveBaseUrl(axiosDefaults);
    const url = /^https?:\/\//i.test(config.url)
      ? config.url
      : `${baseUrl}${config.url.startsWith('/') ? config.url : `/${config.url}`}`;
    const headers = {};
    const rawHeaders = config.headers || {};
    Object.keys(rawHeaders).forEach((key) => {
      if (rawHeaders[key] !== undefined && rawHeaders[key] !== null) {
        headers[key] = rawHeaders[key];
      }
    });
    let body = config.data;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch (e) {
        // keep raw string
      }
    }
    return buildCurlFromParts({
      method: (config.method || 'get').toUpperCase(),
      url,
      headers,
      body: (config.method || 'get').toUpperCase() === 'GET' ? undefined : (body || fallbackPayload)
    });
  }
  if (fallbackPayload) {
    return buildChatAskCurl(fallbackPayload, axiosDefaults);
  }
  return null;
}

export function buildCurlFromFailedRequest(request, axiosDefaults) {
  if (!request) {
    return null;
  }
  const method = (request.method || 'GET').toUpperCase();
  if (method === 'GET') {
    return buildTrackingGetCurl(request.path || request.url, request.query || request.params, axiosDefaults);
  }
  const baseUrl = resolveBaseUrl(axiosDefaults);
  const path = request.path || request.url || '/api/admin/onboarding-chat';
  const url = `${baseUrl}${path.startsWith('/') ? path : `/${path}`}${buildQueryString(request.query || request.params)}`;
  return buildCurlFromParts({
    method,
    url,
    headers: request.headers,
    body: request.body
  });
}

const DEFAULT_PATIENT_API_BASE =
  process.env.VUE_APP_PATIENT_API || process.env.VUE_APP_GPS_API || 'http://localhost:8080';

function parseJsonField(value) {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'object') {
    return value;
  }
  try {
    return JSON.parse(String(value));
  } catch (e) {
    return String(value);
  }
}

/** Build curl for a failed onboarding API_CALL event on the session timeline. */
export function buildEventApiCurl(event, apiBase = DEFAULT_PATIENT_API_BASE) {
  if (!event) {
    return '';
  }
  const meta = event.metadata || {};
  const method = String(meta.httpMethod || 'GET').toUpperCase();
  const apiPath = String(event.apiPath || '').replace(/^\/+/, '');
  if (!apiPath) {
    return '';
  }
  const query = parseJsonField(meta.queryParams) || {};
  const base = String(apiBase || DEFAULT_PATIENT_API_BASE).replace(/\/+$/, '');
  const url = `${base}/${apiPath}${buildQueryString(query)}`;
  const body = parseJsonField(meta.requestPayload);
  return buildCurlFromParts({
    method,
    url,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: method === 'GET' || method === 'HEAD' ? undefined : body
  });
}
